import { useState, useEffect } from 'react'
import { collection, deleteField, getDocs, limit, query } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useFirestoreCrud } from '@/hooks/useFirestoreCrud'
import { omitEmptyValues } from '@/lib/cms-form'
import { BannerSetting, ShopCategory } from '@/types'
import { SectionCard } from '@/components/shared/section-card'
import { DataTable } from '@/components/shared/data-table'
import { StatusBadge } from '@/components/shared/status-badge'
import { ImageUpload } from '@/components/cms/image-upload'
import { ConfirmDeleteDialog } from '@/components/shared/confirm-delete-dialog'
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogContent, DialogFooter, DialogCloseButton } from '@/components/ui/dialog'
import { formatDateTime } from '@/lib/format'
import { Plus, Pencil, Trash2, Save, Search, Loader2, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// ─── Placement options ───────────────────────────────────────────────────────
const PLACEMENTS = ['home_hero', 'shop_banner', 'store_promo', 'article_inline']
const STATUSES   = ['active', 'inactive', 'scheduled']

// ─── Action types with what they deeplink to ─────────────────────────────────
type ActionType = 'product_detail' | 'product_list' | 'product_category' | 'article_detail' | 'article_list' | 'class_detail' | 'class_list' | 'store' | 'external_url' | 'none'

interface ActionTypeConfig {
  value: ActionType
  label: string
  needsId: boolean
  collection?: string
  idField?: string
  labelField?: string
}

const ACTION_TYPES: ActionTypeConfig[] = [
  { value: 'none',             label: 'Tidak ada action',        needsId: false },
  { value: 'store',            label: 'Store (Semua Produk)',     needsId: false },
  { value: 'product_list',     label: 'Product List',            needsId: false },
  { value: 'product_detail',   label: 'Product Detail',          needsId: true, collection: 'shopProducts', labelField: 'name',  idField: 'id' },
  { value: 'product_category', label: 'Product Category',        needsId: false },
  { value: 'article_list',     label: 'Article List',            needsId: false },
  { value: 'article_detail',   label: 'Article Detail',          needsId: true, collection: 'articles',  labelField: 'title', idField: 'id' },
  { value: 'class_list',       label: 'Class List',              needsId: false },
  { value: 'class_detail',     label: 'Class Detail',            needsId: true, collection: 'classes',   labelField: 'title', idField: 'id' },
  { value: 'external_url',     label: 'External URL',            needsId: false },
]
// Kontrak dengan app: setiap `value` di atas wajib dikenali parseBannerAction()
// (lib/services/banner_action.dart) — dijaga oleh test/banner_action_test.dart.

// Field tujuan yang dibaca app untuk tiap action. Field lain dihapus saat simpan
// (upsert memakai merge, jadi nilai lama akan tertinggal kalau hanya dikosongkan).
type TargetField = 'targetId' | 'targetValue' | 'actionUrl'
const TARGET_FIELDS: TargetField[] = ['targetId', 'targetValue', 'actionUrl']

// Banner lama menyimpan nama sebelum Apr 2026; petakan agar tetap bisa diedit.
const LEGACY_ACTION_TYPES: Record<string, ActionType> = {
  article: 'article_detail',
  class: 'class_detail',
  url: 'external_url',
}

function toCurrentActionType(raw: unknown): ActionType {
  const value = typeof raw === 'string' && raw.trim() ? raw.trim() : 'none'
  return (LEGACY_ACTION_TYPES[value] ?? value) as ActionType
}

function isKnownActionType(type: string) {
  return ACTION_TYPES.some(a => a.value === type)
}

function requiredTargetField(type: ActionType): TargetField | null {
  switch (type) {
    case 'product_detail':
    case 'article_detail':
    case 'class_detail':
      return 'targetId'
    case 'product_category':
      return 'targetValue'
    case 'external_url':
      return 'actionUrl'
    default:
      return null
  }
}

// ─── Firestore Picker Component ──────────────────────────────────────────────
function FirestorePicker({ collectionName, labelField, value, onChange }: {
  collectionName: string; labelField: string; value: string
  onChange: (id: string, label: string) => void
}) {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    getDocs(query(collection(db, collectionName), limit(100)))
      .then(snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [collectionName])

  const filtered = items.filter(item =>
    String(item[labelField] || item.name || item.title || item.id)
      .toLowerCase().includes(search.toLowerCase())
  )

  const selectedItem = items.find(i => i.id === value)
  const selectedLabel = selectedItem ? (selectedItem[labelField] || selectedItem.name || selectedItem.title || '') : ''

  return (
    <div className="space-y-2">
      {value && (
        <div className="flex items-center justify-between p-2.5 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <span className="text-sm text-orange-300 font-medium truncate">{selectedLabel}</span>
          <code className="text-xs text-orange-400 font-mono ml-2 shrink-0">{value}</code>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-500" />
        <input type="text" placeholder={`Cari ${collectionName}...`} value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-stone-700 bg-[#0a0a0a] rounded-xl pl-9 pr-4 py-2 text-sm text-stone-100 placeholder-stone-500 focus:outline-none focus:border-orange-500" />
      </div>
      <div className="max-h-36 overflow-y-auto rounded-xl border border-stone-700 divide-y divide-stone-800">
        {loading ? (
          <div className="p-3 text-center"><Loader2 className="w-5 h-5 text-orange-500 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-center text-sm text-stone-500">Data tidak ditemukan</div>
        ) : filtered.map(item => {
          const label = item[labelField] || item.name || item.title || item.id
          return (
            <button key={item.id} type="button"
              onClick={() => onChange(item.id, label)}
              className={cn('w-full px-4 py-2 flex items-center justify-between text-left transition hover:bg-stone-800', item.id === value && 'bg-orange-500/10')}
            >
              <span className="text-sm text-stone-100 truncate">{label}</span>
              {item.id === value && <CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Empty form factory ──────────────────────────────────────────────────────
function emptyForm() {
  return {
    id: '', imageUrl: '', title: '', subtitle: '',
    placement: 'home_hero', cta: '',
    actionType: 'none' as ActionType, targetId: '', targetValue: '', actionUrl: '', status: 'inactive', showInStore: false,
  }
}

// ─── Main Component ──────────────────────────────────────────────────────────
/**
 * Banner ditampilkan di home app dengan tinggi 220pt dan lebar layar dikurangi
 * padding 24pt di kedua sisi. Pada layar 4x (mis. Galaxy S-series) itu berarti
 * sekitar 1700 x 880 piksel fisik, jadi 1600x900 sudah mencukupi tanpa membuat
 * berkasnya kelewat besar.
 */
const BANNER_RECOMMENDED_WIDTH = 1600
const BANNER_RECOMMENDED_HEIGHT = 900
const BANNER_MAX_SIZE = BANNER_RECOMMENDED_WIDTH


export function BannersPage() {
  const banners = useFirestoreCrud<BannerSetting>('banners')
  const [form, setForm] = useState(emptyForm())
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const shopCategories = useFirestoreCrud<ShopCategory>('shopCategories')
  const categoryNames = Array.from(
    new Set(shopCategories.items.map(c => (c.name ?? '').trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b))

  const actionConfig = ACTION_TYPES.find(a => a.value === form.actionType)
  const targetField = requiredTargetField(form.actionType)
  const isActionIncomplete = targetField !== null && !String(form[targetField] ?? '').trim()

  // Target direset hanya saat admin MENGGANTI tipe action (bukan saat membuka
  // Edit — dulu useEffect mengosongkan targetId setiap dialog Edit dibuka).
  const selectActionType = (value: ActionType) =>
    setForm(f => (f.actionType === value
      ? f
      : { ...f, actionType: value, targetId: '', targetValue: '', actionUrl: '' }))

  const startCreate = () => { setEditingId(null); setForm(emptyForm()); setIsOpen(true) }

  const startEdit = (item: BannerSetting) => {
    setEditingId(item.id ?? null)
    setForm({
      id: item.id ?? '',
      imageUrl: (item as any).imageUrl ?? '',
      title: item.title ?? '',
      subtitle: (item as any).subtitle ?? '',
      placement: (item as any).placement ?? 'home_hero',
      cta: (item as any).cta ?? '',
      actionType: toCurrentActionType((item as any).actionType),
      targetId: (item as any).targetId ?? '',
      targetValue: (item as any).targetValue ?? '',
      actionUrl: (item as any).actionUrl ?? '',
      status: (item as any).status ?? 'inactive',
      showInStore: item.showInStore ?? false,
    })
    setIsOpen(true)
  }

  const submit = () => {
    if (isActionIncomplete) return
    const payload = omitEmptyValues({
      ...(form.id ? { id: form.id } : {}),
      imageUrl: form.imageUrl,
      title: form.title,
      subtitle: form.subtitle,
      placement: form.placement || 'home_hero',
      cta: form.cta,
      actionType: form.actionType || 'none',
      targetId: form.targetId,
      targetValue: form.targetValue,
      actionUrl: form.actionUrl,
      status: form.status || 'inactive',
      showInStore: form.showInStore,
    })
    // Hapus field tujuan yang tidak dipakai action ini supaya app tidak membaca
    // sisa tujuan lama (mis. actionUrl lama dipakai sebagai fallback). Tipe yang
    // tidak dikenal dibiarkan apa adanya agar data tidak hilang.
    const staleTargets = isKnownActionType(form.actionType)
      ? Object.fromEntries(
          TARGET_FIELDS.filter(field => field !== targetField).map(field => [field, deleteField()]),
        )
      : {}
    void banners.upsert({ ...payload, ...staleTargets })
    setIsOpen(false)
  }

  const deleteItem = banners.items.find(i => i.id === deleteId)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-[36px] border border-stone-800 bg-[linear-gradient(135deg,#1c1c1c_0%,#0a0a0a_100%)] p-8 text-white shadow-2xl">
        <div className="inline-flex rounded-full border border-rose-500/20 bg-rose-500/10 px-4 py-1 text-xs uppercase tracking-[0.24em] text-rose-400">Marketing</div>
        <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-tight text-stone-100">Banner Manager</h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-400">
          Kelola banner promosi — set deeplink ke produk, artikel, atau kelas tertentu.
        </p>
      </div>

      {/* Table */}
      <SectionCard
        title="Banners"
        description="Manajemen banner global untuk kampanye, promo, dan event Parlent."
        action={
          <button onClick={startCreate}
            className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 transition shadow-lg shadow-orange-500/10">
            <Plus className="h-4 w-4" /> Tambah Banner
          </button>
        }
      >
        <DataTable
          rows={banners.items}
          columns={[
            {
              header: 'Banner',
              render: (row: BannerSetting) => (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-16 overflow-hidden rounded-lg bg-stone-900 border border-stone-800 shrink-0">
                    {(row as any).imageUrl ? (
                      <img src={(row as any).imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-stone-600">IMG</div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-stone-100 text-sm">{row.title || '—'}</p>
                    <p className="text-xs text-stone-500 truncate max-w-[140px]">{(row as any).subtitle || ''}</p>
                  </div>
                </div>
              ),
            },
            {
              header: 'Placement',
              render: (row: BannerSetting) => (
                <span className="text-xs text-stone-400 bg-stone-900 px-2 py-0.5 rounded">{(row as any).placement || '—'}</span>
              ),
            },
            {
              header: 'Action',
              render: (row: BannerSetting) => (
                <div className="text-xs">
                  <span className="text-orange-400">{ACTION_TYPES.find(a => a.value === (row as any).actionType)?.label || '—'}</span>
                  {(row as any).targetId && (
                    <p className="text-stone-600 font-mono mt-0.5 truncate max-w-[100px]">{(row as any).targetId}</p>
                  )}
                </div>
              ),
            },
            { header: 'Status', render: (row: BannerSetting) => <StatusBadge value={(row as any).status || 'inactive'} /> },
            { header: 'Updated', render: (row: BannerSetting) => <span className="text-xs text-stone-500 italic">{formatDateTime(row.updatedAt as any)}</span> },
            {
              header: 'Aksi',
              render: (row: BannerSetting) => (
                <div className="flex gap-2">
                  <button onClick={() => startEdit(row)}
                    className="inline-flex items-center gap-1 rounded-xl border border-stone-800 px-3 py-2 text-xs font-medium text-stone-300 hover:bg-stone-900 transition">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => row.id && setDeleteId(row.id)}
                    className="inline-flex items-center gap-1 rounded-xl border border-rose-900/30 bg-rose-500/5 px-3 py-2 text-xs font-medium text-rose-500 hover:bg-rose-500/10 transition">
                    <Trash2 className="h-3.5 w-3.5" /> Hapus
                  </button>
                </div>
              ),
            },
          ]}
          emptyTitle="Belum ada banner"
          emptyDescription="Tambahkan banner pertama untuk memulai kampanye di aplikasi."
        />
      </SectionCard>

      {/* ─── Create / Edit Dialog ─────────────────────────── */}
      <Dialog open={isOpen} onOpenChange={setIsOpen} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit Banner' : 'Buat Banner Baru'}</DialogTitle>
          <DialogDescription>Atur konten banner dan deeplink destination.</DialogDescription>
          <DialogCloseButton onClick={() => setIsOpen(false)} />
        </DialogHeader>

        <DialogContent className="space-y-5">
          {/* Image */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-stone-200">Banner Image</label>
            <ImageUpload
              folder="cms/banners"
              value={form.imageUrl}
              maxSize={BANNER_MAX_SIZE}
              hint={`Ukuran ideal ${BANNER_RECOMMENDED_WIDTH} x ${BANNER_RECOMMENDED_HEIGHT}px (rasio 16:9). Bagian bawah tertutup gradasi gelap dan teks.`}
              onChange={url => setForm(f => ({ ...f, imageUrl: url }))}
            />
          </div>

          {/* Title & Subtitle */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-stone-200">Judul Banner</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Promo Akhir Tahun"
                className="w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 placeholder-stone-500 focus:border-orange-500 focus:outline-none" />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-stone-200">CTA Label</label>
              <input value={form.cta} onChange={e => setForm(f => ({ ...f, cta: e.target.value }))}
                placeholder="Lihat Sekarang"
                className="w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 placeholder-stone-500 focus:border-orange-500 focus:outline-none" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-stone-200">Subtitle</label>
            <textarea value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
              placeholder="Deskripsi singkat banner..."
              className="min-h-[70px] w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 placeholder-stone-500 focus:border-orange-500 focus:outline-none" />
          </div>

          {/* Placement & Status */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-stone-200">Placement</label>
              <select value={form.placement} onChange={e => setForm(f => ({ ...f, placement: e.target.value }))}
                className="w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 focus:border-orange-500 focus:outline-none appearance-none">
                {PLACEMENTS.map(p => <option key={p} value={p} className="bg-[#1a1a1a]">{p}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-stone-200">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full rounded-2xl border border-stone-700 bg-[#0a0a0a] px-4 py-3 text-sm text-stone-100 focus:border-orange-500 focus:outline-none appearance-none">
                {STATUSES.map(s => <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 rounded-2xl border border-stone-700 bg-stone-900/50">
            <input type="checkbox" id="showInStore" checked={form.showInStore} onChange={e => setForm(f => ({ ...f, showInStore: e.target.checked }))} className="mt-1 w-5 h-5 rounded border-stone-600 bg-stone-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-stone-900 cursor-pointer" />
            <label htmlFor="showInStore" className="text-sm font-medium text-stone-200 select-none cursor-pointer flex-1">
              Tampilkan juga di aplikasi (Halaman Store)
              <p className="text-xs text-stone-400 mt-1 font-normal">Jika dicentang, banner ini akan muncul sebagai carousel auto-slide di Store Page aplikasi.</p>
            </label>
          </div>

          {/* ─── Action / Deeplink ─────────────────────────────── */}
          <div className="space-y-3 p-4 rounded-2xl border border-stone-700 bg-[#0a0a0a]">
            <p className="text-sm font-semibold text-stone-200">Deeplink / Action</p>
            
            {/* Action type grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ACTION_TYPES.map(at => (
                <button key={at.value} type="button"
                  onClick={() => selectActionType(at.value)}
                  className={cn(
                    'px-3 py-2 rounded-xl border text-xs text-left transition',
                    form.actionType === at.value
                      ? 'border-orange-500 bg-orange-500/10 text-orange-300'
                      : 'border-stone-700 text-stone-400 hover:border-stone-600 hover:text-stone-200'
                  )}
                >
                  {at.label}
                </button>
              ))}
            </div>

            {/* Live picker */}
            {actionConfig?.needsId && actionConfig.collection && (
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-stone-300">
                  Pilih {actionConfig.label.replace(' Detail', '')}
                </label>
                <FirestorePicker
                  collectionName={actionConfig.collection}
                  labelField={actionConfig.labelField || 'title'}
                  value={form.targetId}
                  onChange={(id, label) => setForm(f => ({ ...f, targetId: id, targetValue: label }))}
                />
              </div>
            )}

            {/* Category input for product_category */}
            {form.actionType === 'product_category' && (
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-stone-300">Kategori Produk</label>
                {/* Pilih dari shopCategories (sumber yang sama dengan filter Store di app) */}
                <select value={form.targetValue} onChange={e => setForm(f => ({ ...f, targetValue: e.target.value }))}
                  className="w-full rounded-xl border border-stone-700 bg-[#0a0a0a] px-4 py-2.5 text-sm text-stone-100 focus:border-orange-500 focus:outline-none">
                  <option value="">Pilih kategori…</option>
                  {categoryNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                  {form.targetValue && !categoryNames.includes(form.targetValue) && (
                    <option value={form.targetValue}>{form.targetValue} (tidak ada di Commerce)</option>
                  )}
                </select>
              </div>
            )}

            {/* External URL */}
            {form.actionType === 'external_url' && (
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-stone-300">External URL</label>
                <input type="url" value={form.actionUrl} onChange={e => setForm(f => ({ ...f, actionUrl: e.target.value }))}
                  placeholder="https://..."
                  className="w-full rounded-xl border border-stone-700 bg-[#0a0a0a] px-4 py-2.5 text-sm text-stone-100 placeholder-stone-500 focus:border-orange-500 focus:outline-none" />
              </div>
            )}

            {isActionIncomplete && (
              <p className="text-xs text-rose-400">
                Lengkapi tujuan untuk action “{actionConfig?.label ?? form.actionType}” sebelum menyimpan.
              </p>
            )}

            {/* Deeplink summary */}
            {form.actionType !== 'none' && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-stone-500">Deeplink:</span>
                <code className="text-orange-300 font-mono">
                  {form.actionType}{form.targetId ? `/${form.targetId}` : form.targetValue ? `?category=${form.targetValue}` : ''}
                </code>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogFooter>
          <button onClick={() => setIsOpen(false)}
            className="rounded-xl border border-stone-700 px-5 py-2.5 text-sm font-medium text-stone-300 hover:bg-stone-800 transition">
            Batal
          </button>
          <button onClick={submit} disabled={isActionIncomplete}
            className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 transition shadow-lg shadow-orange-500/20 disabled:cursor-not-allowed disabled:opacity-50">
            <Save className="h-4 w-4" />
            {editingId ? 'Simpan' : 'Buat Banner'}
          </button>
        </DialogFooter>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={deleteId !== null}
        itemName={deleteItem?.title || deleteItem?.id}
        onClose={() => setDeleteId(null)}
        onConfirm={() => { if (deleteId) banners.remove(deleteId); setDeleteId(null) }}
      />
    </div>
  )
}
